import React from "react"
import { useContactsLight } from "../../Contacts/hooks/useContacts"
import { Box, Chip, IconButton } from "@mui/material"
import { EditOutlined, DeleteOutlined, AttachFile } from "@mui/icons-material"
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid-pro"
import PropTypes from "prop-types"
import i18n from "i18n"
import DataGridLocaleText from "common/DataGridLocaleText"
import CustomColumnMenu from "./CustomColumnMenu"
import { costTypes } from "../constants"
import { toCents } from "../CostModal/utils/costsUtils"
import useHideDisabledColumns from "hooks/useHideDisabledColumns"

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarQuickFilter
      quickFilterParser={searchInput =>
        searchInput.split(",").map(value => value.trim())
      }
      quickFilterFormatter={quickFilterValues => quickFilterValues.join(", ")}
    />
  </GridToolbarContainer>
)

const CostGrid = ({ costs, onEditCost, onDeleteCost, organizationId }) => {
  useHideDisabledColumns()
  const { data: contactsLight = [] } = useContactsLight(
    organizationId,
    !!organizationId,
  )

  const getProviderLabel = row => {
    const contact = contactsLight.find(c => c.SK === row?.providerId)
    if (row?.provider && String(row.provider).trim()) {
      return String(row.provider).trim()
    }
    if (contact) {
      return `${contact.firstname || ""} ${contact.lastname || ""}`.trim()
    }
    return ""
  }

  const getProjectSearchLabel = row => {
    const names = []

    if (
      row?.createdByProject &&
      typeof row.createdByProject === "object" &&
      row.createdByProject.name
    ) {
      names.push(String(row.createdByProject.name).trim())
    }

    const projects = Array.isArray(row?.projects) ? row.projects : []
    projects.forEach(project => {
      if (typeof project === "string") {
        names.push(project)
      } else if (project?.name) {
        names.push(project.name)
      }
    })

    return [
      ...new Set(names.map(name => String(name).trim()).filter(Boolean)),
    ].join(", ")
  }

  const getProjectDisplayLabel = row => {
    const createdByProject = row?.createdByProject
    if (
      createdByProject &&
      typeof createdByProject === "object" &&
      createdByProject.name
    ) {
      return createdByProject.name
    }

    const projects = Array.isArray(row?.projects) ? row.projects : []
    if (projects.length === 0) {
      return ""
    }
    if (projects.length === 1) {
      const project = projects[0]
      return typeof project === "string" ? project : project?.name || ""
    }
    return `${projects.length} ${i18n.t("costs.columns.projects")}`
  }

  const getOriginLabel = row => row?.purchaseOrderNumber || "Manual"

  const formatAmount = value => {
    if (value != null && value !== "") {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) {
        return `${parsed.toFixed(2)} €`
      }
    }
    return value ?? ""
  }

  const columns = [
    {
      field: "date",
      headerName: i18n.t("costs.columns.date"),
      width: 120,
      renderCell: params => {
        if (!params.value) return ""
        const date = new Date(params.value)
        return date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      },
    },
    {
      field: "title",
      headerName: i18n.t("costs.columns.title"),
      width: 200,
    },
    {
      field: "project",
      headerName: i18n.t("costs.columns.project"),
      width: 150,
      type: "string",
      valueGetter: (_value, row) => getProjectSearchLabel(row),
      renderCell: params => getProjectDisplayLabel(params.row),
    },
    {
      field: "origin",
      headerName: i18n.t("costs.columns.origin"),
      width: 160,
      type: "string",
      valueGetter: (_value, row) => getOriginLabel(row),
      renderCell: params => getOriginLabel(params.row),
    },
    {
      field: "provider",
      headerName: i18n.t("costs.columns.provider"),
      width: 180,
      type: "string",
      valueGetter: (_value, row) => getProviderLabel(row),
      renderCell: params => getProviderLabel(params.row),
    },
    {
      field: "subtotal",
      headerName: i18n.t("costs.columns.subtotal"),
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: params => formatAmount(params.value),
    },
    {
      field: "total",
      headerName: i18n.t("costs.columns.total"),
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: params => formatAmount(params.value),
    },
    {
      field: "expense_type",
      headerName: i18n.t("costs.columns.type"),
      width: 100,
      renderCell: params => {
        if (costTypes.includes(params.value)) {
          return i18n.t(`costs.types.${params.value}`)
        }
        return params.value ?? ""
      },
    },
    {
      field: "is_direct",
      headerName: i18n.t("costs.columns.nature"),
      width: 120,
      renderCell: params => {
        if (params.value === true) {
          return i18n.t("costs.nature.direct")
        }
        if (params.value === false) {
          return i18n.t("costs.nature.indirect")
        }
        return ""
      },
    },
    {
      field: "allocated",
      headerName: i18n.t("costs.columns.allocated"),
      width: 100,
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id)
        const row2 = param2.api.getRow(param2.id)
        const t1 = toCents(row1.total || 0)
        const a1 = toCents(row1.allocated || 0)
        const t2 = toCents(row2.total || 0)
        const a2 = toCents(row2.allocated || 0)
        const perc1 = t1 > 0 ? a1 / t1 : 0
        const perc2 = t2 > 0 ? a2 / t2 : 0
        return perc1 - perc2
      },
      renderCell: params => {
        const allocatedRaw = params.row.allocated || 0
        const subtotalRaw = params.row.subtotal || 0
        const allocatedCents = toCents(allocatedRaw)
        const subtotalCents = toCents(subtotalRaw)
        const ratio = subtotalCents > 0 ? allocatedCents / subtotalCents : 0

        const percentageDisplay = (ratio * 100).toFixed(2)

        const isZero = allocatedCents === 0
        const isComplete =
          subtotalCents > 0 &&
          (allocatedCents >= subtotalCents ||
            subtotalCents - allocatedCents <= 0.5 ||
            ratio >= 0.99995)
        const chipColor = isZero ? "error" : isComplete ? "success" : "warning"
        return (
          <Chip
            label={`${percentageDisplay}%`}
            size="small"
            variant="filled"
            color={chipColor}
            sx={{ width: "85px" }}
          />
        )
      },
    },
    {
      field: "files",
      headerName: i18n.t("costs.columns.documents"),
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: params => {
        const files = params.value
        const count = Array.isArray(files) ? files.length : 0
        if (count === 0) return ""
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              gap: 0.5,
            }}
          >
            <AttachFile fontSize="small" sx={{ opacity: 0.7 }} />
            {count}
          </Box>
        )
      },
    },
    {
      field: "actions",
      headerName: i18n.t("costs.columns.actions"),
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: false,
      align: "center",
      headerAlign: "center",
      pinnable: false,
      renderCell: params => (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              if (onEditCost) {
                onEditCost(params.row)
              }
            }}
            aria-label="edit"
          >
            <EditOutlined fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              if (!onDeleteCost) return
              onDeleteCost(params.row)
            }}
            aria-label="delete"
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const handleRowClick = params => {
    if (onEditCost) {
      onEditCost(params.row)
    }
  }

  const localeText = DataGridLocaleText()

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGridPro
        rows={costs}
        columns={columns}
        getRowId={row => row.expenseId}
        hideFooter
        density="standard"
        disableMultipleRowSelection
        disableColumnReorder
        onRowClick={handleRowClick}
        localeText={localeText}
        disableColumnPinning={false}
        initialState={{
          sorting: {
            sortModel: [{ field: "date", sort: "desc" }],
          },
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        slots={{
          toolbar: CustomToolbar,
          columnMenu: CustomColumnMenu,
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-toolbarContainer>Button": {
            color: "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none",
          },
        }}
      />
    </Box>
  )
}

CostGrid.propTypes = {
  costs: PropTypes.array.isRequired,
  organizationId: PropTypes.string.isRequired,
  onEditCost: PropTypes.func,
  onDeleteCost: PropTypes.func,
}

export default CostGrid
